// TREASURY v2 — the figures and the lines above the groups (UI rebuild batch 2). The words and rules are today's
// (screens/TreasuryBalances.tsx): "≈" on a conversion, "≥" on a floor, `—` for a figure the books did not give, "est."
// once in the strip, amber only where a stated price is stale, and the To-book line built from the rows' own readings.
import { Fragment, createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import type { TreasuryValuation } from '../../../lib/api';
import { displayAsset, fmtAmount } from '../../../lib/format';
import { railName } from '../../../lib/naming';
import { StateLine } from '../kit';
import { approx, fmtEstimate, ageWords, phoneRailName, waitingAmount, type Headline, type RollUp, type ToBook } from './logic';

const FIGURE_SIZE = {
  sm: 'text-meta font-medium',
  md: 'text-body font-semibold',
  figure: 'text-figure font-semibold',
} as const;

/** The cumulative figure for a wallet, a group, or the desk's coins — never a bare number. */
export function RollUpFigure({ r, size = 'md' }: { r: RollUp; size?: keyof typeof FIGURE_SIZE }) {
  // The banner says why, once; the figure reads a quiet dash — never a 0, never blank.
  if (r.kind === 'unavailable') return <span className="text-tg-hint" title="Rates unread">—</span>;
  if (r.kind === 'unpriceable') return <span className="text-meta text-warn">no price for {r.unpriced.map(displayAsset).join(', ')}</span>;
  const figure = r.kind === 'counted' ? fmtAmount(r.amount, r.quote) : fmtEstimate(r.amount, r.quote, r.kind === 'partial');
  return (
    <span className={`font-mono tabular ${FIGURE_SIZE[size]} ${(r.amount ?? 0) < 0 ? 'text-neg' : ''}`}>
      {r.kind === 'counted' ? figure : approx(figure, r.kind === 'partial' ? '≥' : '≈')}{' '}
      <span className="font-sans text-meta font-normal text-tg-hint">{r.quote}</span>
    </span>
  );
}

/** "est. · 8d ago" under a figure: in the strip always, elsewhere only when its stated price is stale; what has no
 *  price is always said. */
export function ValuationNote({ r, strip = false, inline = false }: { r: RollUp; strip?: boolean; inline?: boolean }) {
  const parts: Array<{ key: string; className?: string; text: string }> = [];
  if (r.stated && (strip || r.stale)) {
    const age = ageWords(r.stated.as_of, r.stated.age_hours);
    parts.push({ key: 'stated', className: r.stale ? 'text-warn' : undefined, text: age ? `est. · ${age}` : 'est.' });
  } else if (strip && (r.kind === 'valued' || r.kind === 'partial')) {
    parts.push({ key: 'derived', text: 'est.' });
  }
  if (r.unpriced.length > 0) parts.push({ key: 'unpriced', className: 'text-warn', text: `no price for ${r.unpriced.map(displayAsset).join(', ')}` });
  if (parts.length === 0) return null;
  return (
    <span className={`${inline ? '' : 'block '}font-sans text-meta font-normal text-tg-hint`}>
      {parts.map((p, i) => (
        <Fragment key={p.key}>
          {i > 0 && <span className="opacity-40"> · </span>}
          <span className={`whitespace-nowrap ${p.className ?? ''}`}>{p.text}</span>
        </Fragment>
      ))}
    </span>
  );
}

/** A group's subtotal on one line: its currencies, then its coins as one estimate. */
export function GroupHeadline({ h, className = '' }: { h: Headline; className?: string }) {
  if (h.fiat.length === 0 && !h.crypto) return null;
  return (
    <span className={className}>
      {/* Each currency holds together on a wrap — never a figure stranded from its code (DESIGN-SYSTEM §3). */}
      {h.fiat.map(([a, v], i) => (
        <Fragment key={a}>{i > 0 && <span className="opacity-40"> · </span>}<span className="whitespace-nowrap"><span className="font-mono tabular">{fmtAmount(v, a)}</span> {a}</span></Fragment>
      ))}
      {h.fiat.length > 0 && h.crypto && <span className="opacity-40"> · </span>}
      {h.crypto && <RollUpFigure r={h.crypto} size="sm" />}
    </span>
  );
}

const BOX = {
  warn: 'border-warn/25 bg-warn-bg',
  bad: 'border-neg/25 bg-neg-bg',
  neutral: 'border-stroke bg-panel-2',
} as const;

/** One boxed line that leads the page (the kit's Notice look) with room for a StateLine's Details. */
export function Box({ tone, children }: { tone: keyof typeof BOX; children: ReactNode }) {
  return <div className={`mb-4 rounded-lg border px-4 py-3 lg:mb-5 ${BOX[tone]}`}>{children}</div>;
}

/** A failed valuation read is not a settled fact: said once, at the top, with Try again. */
export function ValuationBanner({ valuation, onRetry }: { valuation?: TreasuryValuation; onRetry: () => void }) {
  if (!valuation) return null;
  const retry = <button type="button" className="btn-link text-body" onClick={onRetry}>Try again</button>;
  if (!valuation.available) {
    const e = valuation.error;
    const why = e ? `${e.charAt(0).toUpperCase()}${e.slice(1)}${/[.!?]$/.test(e) ? '' : '.'}` : '';
    return (
      <Box tone="warn">
        <StateLine tone="warn" action={retry} more={<>{why && <p>{why}</p>}<p>Every quantity below is exact.</p></>}>
          Couldn&rsquo;t read the rate book — no wallet totals.
        </StateLine>
      </Box>
    );
  }
  if (!valuation.stated_available) {
    return (
      <Box tone="warn">
        <StateLine tone="warn" action={retry} more="A failed read, not a zero.">
          Couldn&rsquo;t read stated prices — untraded coins show no value.
        </StateLine>
      </Box>
    );
  }
  return null;
}

/** An account's name: whole on the laptop, without the desk's own prefix on the phone. */
export function AccountName({ row, desk }: { row: { slug?: string | null; label?: string | null }; desk: string }) {
  const whole = railName(row);
  const short = phoneRailName(row, desk);
  if (whole === short) return <>{whole}</>;
  return <><span className="lg:hidden">{short}</span><span className="hidden lg:inline">{whole}</span></>;
}

/** MONEY WAITING FOR A BOOKING, ONE AMBER BOX ABOVE THE GROUPS: the first reading and "+N more"; every account behind
 *  Details. No reading, no box. */
export function ToBookLine({ items, desk }: { items: ToBook[]; desk: string }) {
  if (items.length === 0) return null;
  const readings = items.flatMap((it) => it.waiting.map((w) => ({ it, w })));
  const first = readings[0]!;
  const others = readings.length - 1;
  return (
    <Box tone="warn">
      <StateLine
        tone="warn"
        more={others > 0 ? items.map((it) => <p key={it.key}>{it.waiting.map(waitingAmount).join(' + ')} on <AccountName row={it.row} desk={desk} /></p>) : undefined}
      >
        {/* The words left, the figure in its own seat right (his "one fact per place", 1 Nov 2026). */}
        <span className="flex flex-wrap items-baseline justify-between gap-x-4">
          <span>To book on <AccountName row={first.it.row} desk={desk} />{others > 0 ? ` +${others} more` : ''}</span>
          <span className="whitespace-nowrap font-mono tabular">{waitingAmount(first.w)}</span>
        </span>
      </StateLine>
    </Box>
  );
}

// ── One account, opened by link (/treasury?rail=<slug>): its group and fold open, its row scrolled to and marked ─────
export const FocusRail = createContext<string | null>(null);
export const FOCUS_ROW = 'relative bg-brand-gold/10';
/** The gold edge of the row a link asked for — a child bar, not a border (a divide repaints borders). */
export function FocusBar() {
  return <span aria-hidden className="absolute inset-y-0 left-0 z-10 w-1 bg-brand-gold" />;
}
export function useFocusRow(slug: string | null | undefined) {
  const want = useContext(FocusRail);
  const focused = !!want && slug === want;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);
  return { focused, ref };
}

/** INTO THE HISTORY OF ONE ACCOUNT: Money movements filtered to it. Drawn on the phone; on the laptop when the row is
 *  hovered or the link has the keyboard. Without an account, an empty cell of the same width (the columns stay put). */
export function RailHistory({ slug, visible = false }: { slug?: string | null; visible?: boolean }) {
  if (!slug) return <span aria-hidden className="w-11 shrink-0 lg:w-10" />;
  return (
    <Link
      to={`/movements?rail=${encodeURIComponent(slug)}`}
      title="Money movements on this account"
      aria-label="Money movements on this account"
      className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg text-tg-hint transition-colors hover:text-tg-link lg:h-10 lg:w-10 ${visible ? '' : 'lg:opacity-0 lg:group-hover/row:opacity-100 lg:focus-visible:opacity-100'}`}
    >
      <History className="h-4 w-4" aria-hidden />
    </Link>
  );
}
